/**
 * User Edit Modal Component
 *
 * Modal for editing a tenant user's role.
 *
 * @fileoverview Edit user modal component
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  VStack,
  Text,
  Avatar,
  HStack,
  Box,
  useColorModeValue,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import type { TenantUser, UpdateTenantUserInput, TenantRole } from '../../types/users';

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: TenantUser | null;
  onSubmit: (userId: string, data: UpdateTenantUserInput) => Promise<void>;
  loading: boolean;
  error: string | null;
}

/**
 * User Edit Modal Component
 *
 * Modal dialog for editing a tenant user's role.
 */
export const UserEditModal: React.FC<UserEditModalProps> = ({
  isOpen,
  onClose,
  user,
  onSubmit,
  loading,
  error,
}) => {
  const bgColor = useColorModeValue('white', 'xacto.deepNavy');
  const textColor = useColorModeValue('gray.900', 'xacto.pureWhite');
  const mutedColor = useColorModeValue('gray.500', 'xacto.silverGray');
  // Hoisted above the `if (!user) return null` below — see
  // DeactivateUserModal/RemoveUserModal for the same fix and why: this
  // modal stays mounted with `user` toggling between an object and null,
  // so calling this only in the reachable-after-the-guard JSX changed the
  // hook count for this instance between renders.
  const userCardBg = useColorModeValue('gray.50', 'xacto.darkSurface');

  const [tenantRole, setTenantRole] = useState<TenantRole>('USER');

  // Update form when user changes
  useEffect(() => {
    if (user) {
      setTenantRole(user.tenantRole);
    }
  }, [user]);

  const getDisplayName = (u: TenantUser) => {
    if (u.user.displayName) return u.user.displayName;
    if (u.user.firstName || u.user.lastName) {
      return `${u.user.firstName || ''} ${u.user.lastName || ''}`.trim();
    }
    return u.user.email.split('@')[0];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    await onSubmit(user.userId, { tenantRole });
  };

  const handleClose = () => {
    if (user) {
      setTenantRole(user.tenantRole);
    }
    onClose();
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>Edit User Role</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              {/* User info display */}
              <HStack spacing={3} p={3} borderRadius="md" bg={userCardBg}>
                <Avatar size="md" name={getDisplayName(user)} />
                <Box>
                  <Text fontWeight="medium" color={textColor}>
                    {getDisplayName(user)}
                  </Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {user.user.email}
                  </Text>
                </Box>
              </HStack>

              <FormControl>
                <FormLabel>Role</FormLabel>
                <Select
                  value={tenantRole}
                  onChange={(e) => setTenantRole(e.target.value as TenantRole)}
                >
                  <option value="USER">Member</option>
                  <option value="TENANT_ADMIN">Admin</option>
                </Select>
                <FormHelperText>
                  Admins can manage tenant users and settings
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              loadingText="Saving..."
              isDisabled={tenantRole === user.tenantRole}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UserEditModal;
